import { memo, useEffect } from 'react';
import Box from '../Box/Box';
import { ProgressBarProps } from './ProgressBar.types';

import {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSequence,
} from 'react-native-reanimated';

function ProgressBar({
  from,
  to,
  progress,
  level = 0,
  animationDuration = 500,
  gradientType,
  ...rest
}: ProgressBarProps) {
  const percent = Math.min((progress - from) / (to - from), 1);
  const progressWidth = useSharedValue(percent * 100);
  const prevLevel = useSharedValue(level);

  // TODO: Add Box Shadow

  useEffect(() => {
    if (level && level !== prevLevel.value && level > prevLevel.value) {
      // Fill the bar fully, then reset to 0 , then fill to the new level
      progressWidth.value = withSequence(
        withTiming(100, { duration: animationDuration }), // fill full width
        withTiming(0, { duration: 0 }), // reset to 0
        withTiming(percent * 100, { duration: animationDuration }) // fill to new level
      );
      prevLevel.value = level;
    } else {
      progressWidth.value = withTiming(percent * 100, {
        duration: animationDuration,
      });
    }
  }, [progress, level]);

  useEffect(() => {
    if (prevLevel.value > level) {
      prevLevel.value = level;
    }
  }, [level]);

  const animatedStyle = useAnimatedStyle(() => ({
    left: '-100%',
    transform: [{ translateX: `${progressWidth.value}%` }],
  }));

  return (
    <Box
      width={'100%'}
      height={'space-200'}
      borderRadius="radius-full"
      backgroundColor="screenBackground" // in figma it says bg-default. how to determine which color to use?
      overflow="hidden"
      {...rest}
    >
      <Box
        height="100%"
        width="100%"
        borderRadius="radius-full"
        variant="gradient"
        gradient={gradientType ?? 'darkSurfaceBrand'}
        animatedStyle={animatedStyle}
        position="relative"
      />
    </Box>
  );
}

export default memo(ProgressBar);
